import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDown } from "~/components/icons/ChevronDown";
import { Box, Flex } from "@radix-ui/themes";
import styles from "./sidebar-menu.module.css";
import { Link } from "~/components/RouterLink";

import {
	Locale,
	type LocalizedStrings,
	useLocalization,
} from "~/components/localization/LocalizationProvider";

type Localization = {
	aboutUsLabel: string;
	meetTheDoctors: string;
	meetTheTeam: string;
	officeTour: string;
	newPatientsLabel: string;
	whyChooseUs: string;
	kidsClub: string;
	freeConsult: string;
	yourFirstVisit: string;
	insuranceAndFinance: string;
	ourTreatmentsLabel: string;
	invisalign: string;
	invisalignForTeens: string;
	invisalignMiami: string;
	metalBraces: string;
	clearBraces: string;
	orthodonticsForAllAges: string;
	adults: string;
	teens: string;
	children: string;
	orthodonticInfo: string;
	orthoFaqs: string;
	orthoEmergency: string;
	orthoCare: string;
	lifeWithBraces: string;
	patientStoriesLabel: string;
	contactUsLabel: string;
	patientsReferAFriend: string;
	ourLocation: string;
	blog: string;
	careers: string;
	covid19: string;
};

const englishLocalization: Localization = {
	aboutUsLabel: "About Us",
	meetTheDoctors: "Meet the Doctors",
	meetTheTeam: "Meet the Team",
	officeTour: "Office Tour",
	newPatientsLabel: "New Patients",
	whyChooseUs: "Why Choose Us",
	kidsClub: "Kids Club",
	freeConsult: "Free Consult",
	yourFirstVisit: "Your First Visit",
	insuranceAndFinance: "Insurance and Finance",
	ourTreatmentsLabel: "Our Treatments",
	invisalign: "Invisalign",
	invisalignForTeens: "Invisalign for Teens",
	invisalignMiami: "Invisalign Miami",
	metalBraces: "Metal Braces",
	clearBraces: "Clear Braces",
	orthodonticsForAllAges: "Orthodontics for All Ages",
	adults: "Adults",
	teens: "Teens",
	children: "Children",
	orthodonticInfo: "Orthodontic Info",
	orthoFaqs: "Ortho FAQs",
	orthoEmergency: "Orthodontic Emergency",
	orthoCare: "Orthodontic Care",
	lifeWithBraces: "Life with Braces",
	patientStoriesLabel: "Patient Stories",
	contactUsLabel: "Contact Us",
	patientsReferAFriend: "Patients: Refer a Friend",
	ourLocation: "Our Location",
	blog: "Blog",
	careers: "Careers",
	covid19: "COVID-19",
};

const spanishLocalization: Localization = {
	aboutUsLabel: "Sobre nosotros",
	meetTheDoctors: "Conozca a los médicos",
	meetTheTeam: "Conozca al equipo",
	officeTour: "Recorrido por la oficina",
	newPatientsLabel: "Nuevos pacientes",
	whyChooseUs: "Por qué elegirnos",
	kidsClub: "Club de niños",
	freeConsult: "Consulta gratuita",
	yourFirstVisit: "Su primera visita",
	insuranceAndFinance: "Seguro y financiamiento",
	ourTreatmentsLabel: "Nuestros tratamientos",
	invisalign: "Invisalign",
	invisalignForTeens: "Invisalign para adolescentes",
	invisalignMiami: "Invisalign Miami",
	metalBraces: "Brackets metálicos",
	clearBraces: "Brackets transparentes",
	orthodonticsForAllAges: "Ortodoncia para todas las edades",
	adults: "Adultos",
	teens: "Adolescentes",
	children: "Niños",
	orthodonticInfo: "Información de ortodoncia",
	orthoFaqs: "Preguntas frecuentes de ortodoncia",
	orthoEmergency: "Emergencia ortodóntica",
	orthoCare: "Cuidado ortodóntico",
	lifeWithBraces: "La vida con brackets",
	patientStoriesLabel: "Historias de pacientes",
	contactUsLabel: "Contáctenos",
	patientsReferAFriend: "Pacientes: Referir a un amigo",
	ourLocation: "Nuestra ubicación",
	blog: "Blog",
	careers: "Empleos",
	covid19: "COVID-19",
};

const localized: LocalizedStrings<Localization> = {
	[Locale.EN]: englishLocalization,
	[Locale.ES]: spanishLocalization,
};

export function SidebarMenu() {
	const getLocalization = useLocalization(localized);
	return (
		<div className={styles.sidebarMenu}>
			<Accordion.Root type="single" collapsible className={styles.menuRoot}>
				{/* About us */}
				<Accordion.Item value="about-us" className={styles.accordionItem}>
					<Accordion.Header className={styles.accordionHeader}>
						<AccordionTrigger
							label={getLocalization("aboutUsLabel")}
							to="/about-us"
						/>
					</Accordion.Header>
					<Accordion.Content forceMount className={styles.accordionContent}>
						<Flex direction="column" gap="0" justify="between">
							<Link className={styles.sidebarLink} to="/meet-our-doctors">
								{getLocalization("meetTheDoctors")}
							</Link>
							<Link className={styles.sidebarLink} to="/meet-the-team">
								{getLocalization("meetTheTeam")}
							</Link>
							<Link className={styles.sidebarLink} to="/office-tour">
								{getLocalization("officeTour")}
							</Link>
						</Flex>
					</Accordion.Content>
				</Accordion.Item>
				{/* New patients */}
				<Accordion.Item value="new-patients" className={styles.accordionItem}>
					<Accordion.Header className={styles.accordionHeader}>
						<AccordionTrigger
							label={getLocalization("newPatientsLabel")}
							to="/new-patients"
						/>
					</Accordion.Header>
					<Accordion.Content forceMount className={styles.accordionContent}>
						<Flex direction="column" gap="0" justify="between">
							<Link className={styles.sidebarLink} to="/why-choose-us">
								{getLocalization("whyChooseUs")}
							</Link>
							<Link className={styles.sidebarLink} to="/kids-club">
								{getLocalization("kidsClub")}
							</Link>
							<Link className={styles.sidebarLink} to="/free-consult">
								{getLocalization("freeConsult")}
							</Link>
							<Link className={styles.sidebarLink} to="/your-first-visit">
								{getLocalization("yourFirstVisit")}
							</Link>
							<Link className={styles.sidebarLink} to="/insurance-finances">
								{getLocalization("insuranceAndFinance")}
							</Link>
							<Link className={styles.sidebarLink} to="/covid-19">
								{getLocalization("covid19")}
							</Link>
						</Flex>
					</Accordion.Content>
				</Accordion.Item>
				{/* Our Treatments */}
				<Accordion.Item value="our-treatments" className={styles.accordionItem}>
					<Accordion.Header className={styles.accordionHeader}>
						<AccordionTrigger
							label={getLocalization("ourTreatmentsLabel")}
							to="/our-treatments"
						/>
					</Accordion.Header>
					<Accordion.Content forceMount className={styles.accordionContent}>
						<Flex direction="column" gap="0" justify="between">
							<Link className={styles.sidebarLink} to="/invisalign">
								{getLocalization("invisalign")}
							</Link>
							<Link className={styles.sidebarLink} to="/invisalign-for-teens">
								{getLocalization("invisalignForTeens")}
							</Link>
							<Link className={styles.sidebarLink} to="/invisalign-miami-fl">
								{getLocalization("invisalignMiami")}
							</Link>
							<Link className={styles.sidebarLink} to="/metal-braces">
								{getLocalization("metalBraces")}
							</Link>
							<Link className={styles.sidebarLink} to="/clear-braces">
								{getLocalization("clearBraces")}
							</Link>
							<Accordion.Root type={"single"} collapsible>
								<Accordion.Item
									value={"orthodontics-for-all-ages"}
									className={styles.accordionItem}
								>
									<Accordion.Header className={styles.nestedAccordionHeader}>
										<NestedAccordionTrigger
											to="/orthodontics-for-all-ages"
											label={getLocalization("orthodonticsForAllAges")}
										/>
									</Accordion.Header>
									<Accordion.Content
										forceMount
										className={styles.nestedAccordionContent}
									>
										<Flex direction="column" gap="0" my="2">
											<Link
												to="/orthodontics-for-adults"
												className={styles.sidebarLink}
											>
												{getLocalization("adults")}
											</Link>
											<Link
												to="/orthodontics-for-teens"
												className={styles.sidebarLink}
											>
												{getLocalization("teens")}
											</Link>
											<Link
												to="/orthodontics-for-children"
												className={styles.sidebarLink}
											>
												{getLocalization("children")}
											</Link>
										</Flex>
									</Accordion.Content>
								</Accordion.Item>
							</Accordion.Root>
							<Accordion.Root type={"single"} collapsible>
								<Accordion.Item
									value={"orthodontic-info"}
									className={styles.accordionItem}
								>
									<Accordion.Header className={styles.nestedAccordionHeader}>
										<NestedAccordionTrigger
											to="/ortho-info"
											label={getLocalization("orthodonticInfo")}
										/>
									</Accordion.Header>
									<Accordion.Content
										forceMount
										className={styles.nestedAccordionContent}
									>
										<Flex direction="column" gap="0" mt="1">
											<Link to="/faqs" className={styles.sidebarLink}>
												{getLocalization("orthoFaqs")}
											</Link>
											<Link
												to="/ortho-emergencies"
												className={styles.sidebarLink}
											>
												{getLocalization("orthoEmergency")}
											</Link>
											<Link to="/ortho-care" className={styles.sidebarLink}>
												{getLocalization("orthoCare")}
											</Link>
											<Link
												to="/life-with-braces"
												className={styles.sidebarLink}
											>
												{getLocalization("lifeWithBraces")}
											</Link>
										</Flex>
									</Accordion.Content>
								</Accordion.Item>
							</Accordion.Root>
						</Flex>
					</Accordion.Content>
				</Accordion.Item>
				{/* Patient Stories */}
				<Box width="100%">
					<Link to="/testimonials" className={styles.accordionLink}>
						{getLocalization("patientStoriesLabel")}
					</Link>
				</Box>
				{/* Contact Us */}
				<Accordion.Item value="contact-us" className={styles.accordionItem}>
					<Accordion.Header className={styles.accordionHeader}>
						<AccordionTrigger
							label={getLocalization("contactUsLabel")}
							to="/contact-us"
						/>
					</Accordion.Header>
					<Accordion.Content forceMount className={styles.accordionContent}>
						<Flex direction="column" gap="0" justify="between">
							<Link className={styles.sidebarLink} to="/free-consult">
								{getLocalization("freeConsult")}
							</Link>
							<Link
								className={styles.sidebarLink}
								to="/patients-refer-a-friend"
							>
								{getLocalization("patientsReferAFriend")}
							</Link>
						</Flex>
					</Accordion.Content>
				</Accordion.Item>
			</Accordion.Root>
			<Flex direction="column" mb="5">
				<Link to="/blog" className={styles.sidebarLink}>
					{getLocalization("blog")}
				</Link>
				<Link to="/careers" className={styles.sidebarLink}>
					{getLocalization("careers")}
				</Link>
			</Flex>
			<div className={styles.contactInfo}>
				<Link to="/free-consult" className={styles.sidebarCta}>
					{getLocalization("freeConsult")}
				</Link>
				<p className={styles.location}>
					<strong>{getLocalization("ourLocation")}</strong>
					<br />
					<a
						href="https://goo.gl/maps/uKex5kBv4Sqja5iY9"
						target="_blank"
						rel="noreferrer"
					>
						8525 SW 92nd Street, Suite B-9
						<br />
						Miami, FL 33156
					</a>
				</p>
				<p className={styles.phone}>
					<a href="tel:305-407-9000">305-407-9000</a>
				</p>
			</div>
		</div>
	);
}

export function AccordionTrigger({ to, label }: { to: string; label: string }) {
	return (
		<span
			className={`${styles.accordionTrigger} ${styles.accordionTriggerInner}`}
		>
			<Link to={to} className={styles.accordionTriggerLabel}>
				{label}
			</Link>
			<Accordion.Trigger className={styles.submenuToggle} aria-label={label}>
				<span className={styles.accordionChevron}>
					<ChevronDown size={20} />
				</span>
			</Accordion.Trigger>
		</span>
	);
}

export function NestedAccordionTrigger({
	to,
	label,
}: {
	to: string;
	label: string;
}) {
	return (
		<span
			className={`${styles.nestedAccordionTrigger} ${styles.nestedAccordionTriggerInner}`}
		>
			<Link to={to} className={styles.accordionTriggerLabel}>
				{label}
			</Link>
			<Accordion.Trigger className={styles.submenuToggle} aria-label={label}>
				<span className={styles.accordionChevron}>
					<ChevronDown size={20} />
				</span>
			</Accordion.Trigger>
		</span>
	);
}
